import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  deleteShiftMutation,
  getDailyScheduleOptions,
  syncShiftsOnDemandOptions,
} from '@/api/main/@tanstack/react-query.gen'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { DailyScheduleResponse, ShiftsByVehicle, ShiftResponse } from '@/api/main/types.gen'
import { dateSpanToArray } from '@/utils/datetime'
import { useEffect, useState } from 'react'
import { useMediaQuery } from '@mantine/hooks'
import { isEmpty } from 'lodash-es'

// Configure dayjs plugins
dayjs.extend(utc)
dayjs.extend(timezone)

// Extended shift type with only the computed properties we need for UI
export interface ExtendedShift extends ShiftResponse {
  startDateTime: dayjs.Dayjs
  endDateTime: dayjs.Dayjs
  isOverlapping: boolean
  overlapPosition: 'top' | 'bottom'
}

export interface ExtendedVehicleShifts extends Omit<ShiftsByVehicle, 'shifts'> {
  shifts: ExtendedShift[]
}

export const useShiftsData = (startDate: Date, endDate: Date) => {
  const queryClient = useQueryClient()
  const isMobile = useMediaQuery('(max-width: 768px)')

  const [syncShiftsOnDemandQueryError, setSyncShiftsOnDemandQueryError] = useState<string | null>(null)
  const [filteredChannel, setFilteredChannel] = useState<string | null>(null)

  const dates = dateSpanToArray(dayjs(startDate).tz('Europe/Prague'), dayjs(endDate).tz('Europe/Prague'))

  const {
    data: shiftsDataArray,
    isLoading,
    error,
    refetch,
  } = useQueries({
    queries: dates.map(date => getDailyScheduleOptions({ path: { schedule_date: date } })),
    combine: results => ({
      data: results.map(result => result.data),
      pending: results.some(result => result.isPending),
      error: results.find(result => result.error)?.error || null,
      isLoading: results.some(result => result.isLoading),
      refetch: () => Promise.all(results.map(result => result.refetch())),
    }),
  })

  const {
    refetch: syncShiftsOnDemand,
    error: syncShiftsOnDemandError,
    isLoading: syncShiftsOnDemandLoading,
  } = useQuery({
    ...syncShiftsOnDemandOptions(),
    enabled: false,
  })

  const { mutate: deleteShift } = useMutation(deleteShiftMutation())

  // Auto-hide error after 5 seconds
  useEffect(() => {
    if (syncShiftsOnDemandError) {
      setSyncShiftsOnDemandQueryError('Failed to sync shifts')

      const timer = setTimeout(() => {
        setSyncShiftsOnDemandQueryError(null)
      }, 5000)

      return () => clearTimeout(timer)
    } else {
      setSyncShiftsOnDemandQueryError(null)
    }
  }, [syncShiftsOnDemandError])

  // reset error when component unmounts
  useEffect(() => {
    return () => {
      setSyncShiftsOnDemandQueryError(null)
      queryClient.resetQueries({
        queryKey: syncShiftsOnDemandOptions().queryKey,
      })
    }
  }, [queryClient])

  const syncShifts = () =>
    syncShiftsOnDemand().then(() => {
      refetch()
    })

  const handleDeleteShift = (shift_id: number) => {
    deleteShift(
      { path: { shift_id } },
      {
        onSuccess: () => {
          refetch()
        },
      }
    )
  }

  // Transform API data to our internal format
  const processShiftsData = (apiData?: DailyScheduleResponse) => {
    if (!apiData) {
      return { vehicleShifts: [], allChannels: [] }
    }

    // Get all unique vehicle IDs
    const allChannels = apiData.vehicle_shifts.map(vs => vs.vehicle_id).sort()

    // Process each vehicle's shifts
    const vehicleShifts: ExtendedVehicleShifts[] = apiData.vehicle_shifts.map(({ shifts, vehicle_id }) => {
      const processedShifts: ExtendedShift[] = shifts
        .map(({ id, interval: { ending_at, starting_from }, driver }) => {
          const startDateTime = dayjs.utc(starting_from).tz('Europe/Prague')
          const endDateTime = dayjs.utc(ending_at).tz('Europe/Prague')

          // Skip invalid shifts (same start/end time or zero duration)
          if (startDateTime.isSame(endDateTime)) {
            return null
          }

          // Skip shifts that are too short (less than 15 minutes)
          const duration = endDateTime.diff(startDateTime, 'minute')
          if (duration < 15) {
            return null
          }

          return {
            id,
            interval: { starting_from, ending_at },
            driver,
            startDateTime,
            endDateTime,
            isOverlapping: false, // Will be calculated below
          }
        })
        .filter(Boolean) as ExtendedShift[]

      // Check for overlapping shifts within the same vehicle
      const sortedShifts = processedShifts.sort((a, b) => a.startDateTime.valueOf() - b.startDateTime.valueOf())

      for (let i = 0; i < sortedShifts.length; i++) {
        for (let j = i + 1; j < sortedShifts.length; j++) {
          const shiftA = sortedShifts[i]
          const shiftB = sortedShifts[j]

          // Check if shifts overlap
          if (shiftA.endDateTime.isAfter(shiftB.startDateTime) && shiftA.startDateTime.isBefore(shiftB.endDateTime)) {
            shiftA.isOverlapping = true
            shiftB.isOverlapping = true

            // Mark the shorter shift to be positioned above
            const durationA = shiftA.endDateTime.diff(shiftA.startDateTime, 'minute')
            const durationB = shiftB.endDateTime.diff(shiftB.startDateTime, 'minute')

            if (durationA < durationB) {
              shiftA.overlapPosition = 'top'
            } else {
              shiftB.overlapPosition = 'top'
            }
          }
        }
      }

      return {
        vehicle_id,
        shifts: processedShifts,
      }
    })

    return {
      vehicleShifts,
      allChannels,
    }
  }

  const dayShifts = shiftsDataArray.map(e => ({
    ...processShiftsData(e),
    timelineStart: dayjs(e?.bounds.starting_from).tz('Europe/Prague'),
  }))

  useEffect(() => {
    if (isMobile && !isEmpty(dayShifts) && dayShifts[0].vehicleShifts.length > 0) {
      setFilteredChannel(dayShifts[0].vehicleShifts[0].vehicle_id)
    } else {
      setFilteredChannel(null)
    }
  }, [isMobile, dayShifts])

  return {
    isLoading,
    error,
    refetch,
    handleDeleteShift,
    dayShifts,
    syncShifts,
    syncShiftsOnDemandQueryError,
    syncShiftsOnDemandLoading,
    filteredChannel,
    setFilteredChannel,
    isMobile,
  }
}
